import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { apiClient } from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';

/** Shared max width for BOT Keys and BOT Status tiles on desktop (full width on small screens). */
export const BOT_SECTION_MAX_WIDTH = 520;

export const BotKeys: React.FC = () => {
  const company = useSelector((state: RootState) => state.authentication.company);
  const { isUserSuperAdmin, hasInstantUpdateAccess } = useModulePermissions();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [clientSecret, setClientSecret] = useState(company?.clientSecret || '');

  const canView = hasInstantUpdateAccess('admin') || isUserSuperAdmin;
  const canRefresh = isUserSuperAdmin || (hasInstantUpdateAccess('admin') && company?.configurations?.defaultBotManager === 'ClientManaged');

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    setSnackbar({ open: true, message: `${label} copied!`, severity: 'success' });
  };

  const handleRefreshSecret = async () => {
    setLoading(true);
    try {
      const data = await apiClient.patch(`/company/${company._id}/refresh-client-secret`);
      if (data?.data?.clientSecret) {
        setClientSecret(data.data.clientSecret);
        setSnackbar({ open: true, message: 'Client Secret refreshed!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: data?.message || 'Failed to refresh client secret', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error refreshing client secret', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!canView) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary">
          You don't have permission to access BOT Keys.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      sx={{ mb: 3, maxWidth: BOT_SECTION_MAX_WIDTH }}
    >
      <Card sx={{ borderRadius: '12px', boxShadow: 3, width: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            BOT Keys
          </Typography>

          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={2} sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Client ID
                </Typography>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
                    {company?._id || '—'}
                  </Typography>
                  <IconButton
                    onClick={() => handleCopy(company?._id || '', 'Client ID')}
                    size="small"
                    aria-label="Copy Client ID"
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Client Secret
                </Typography>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
                    {clientSecret ? '•'.repeat(Math.min(clientSecret.length, 20)) : '—'}
                  </Typography>
                  <IconButton
                    onClick={() => handleCopy(clientSecret, 'Client Secret')}
                    size="small"
                    aria-label="Copy Client Secret"
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {canRefresh && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Actions
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Refresh />}
                      onClick={handleRefreshSecret}
                      disabled={loading}
                      size="small"
                      fullWidth
                      sx={{ maxWidth: { sm: 200 } }}
                    >
                      {loading ? <CircularProgress size={18} /> : 'Refresh'}
                    </Button>
                  </Box>
                </>
              )}
            </Stack>
          </Card>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
